import React, { useState, useRef, useCallback } from 'react';
import { Upload, ChevronLeft, Shield, Search, CheckCircle2, AlertTriangle, Music2, X, Headphones } from 'lucide-react';
import '../../styles/producer.css';
import { API_BASE as API } from '../../lib/apiBase';

export default function ProducerUploadFlow({ onComplete, onBack, token }) {
  const [step, setStep] = useState('upload'); // upload | legal | scanning | done
  const [file, setFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    const valid = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/m4a', 'audio/mp4'];
    if (!valid.some(t => selectedFile.type.includes(t.split('/')[1])) && !selectedFile.name.match(/\.(mp3|wav|ogg|flac|aac|m4a)$/i)) {
      setScanError('Formato no soportado. Sube MP3, WAV, OGG, FLAC o AAC.');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setScanError('Archivo demasiado grande. Máximo 50MB.');
      return;
    }

    setFile(selectedFile);
    setScanError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(p => Math.min(p + Math.random() * 15, 90));
      }, 200);

      const res = await fetch(`${API}/api/upload/audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      clearInterval(progressInterval);

      if (res.ok) {
        const data = await res.json();
        setAudioUrl(data.url);
        setUploadProgress(100);
        setTimeout(() => setStep('legal'), 400);
      } else {
        setScanError('Error subiendo el archivo. Inténtalo de nuevo.');
      }
    } catch {
      setScanError('Error de conexión.');
    } finally {
      setUploading(false);
    }
  }, [token]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) handleFile(droppedFile);
  };

  const handleLegalAndScan = async () => {
    if (!legalAccepted) return;

    // Accept legal terms
    try {
      await fetch(`${API}/api/legal/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: 'artist_terms' }),
      });
    } catch { /* non-blocking */ }

    setStep('scanning');
    setScanError(null);

    // Upload-for-improve: scan + create session with reference audio
    try {
      const res = await fetch(`${API}/api/producer/upload-for-improve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio_url: audioUrl, filename: file?.name || '' }),
      });

      if (res.ok) {
        const data = await res.json();
        setScanResult(data);
        if (!data.allowed) {
          setScanError(data.message || 'Contenido protegido detectado.');
          setStep('legal');
        } else {
          setStep('done');
        }
      } else {
        setScanResult({ status: 'skipped', message: 'Scan no disponible' });
        setStep('done');
      }
    } catch {
      setScanResult({ status: 'skipped', message: 'Scan no disponible' });
      setStep('done');
    }
  };

  const handleEnterStudio = () => {
    onComplete({
      audioUrl: audioUrl.startsWith('http') ? audioUrl : `${API}${audioUrl}`,
      scanResult,
      fileName: file?.name || 'audio',
      sessionId: scanResult?.session_id || null,
      referenceAudioUrl: scanResult?.reference_audio_url || (audioUrl.startsWith('http') ? audioUrl : `${API}${audioUrl}`),
      mode: 'improve',
    });
  };

  return (
    <div className="fixed inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center px-6 relative producer-noise z-[60]">
      {/* Back button */}
      <button
        onClick={onBack}
        className="absolute top-6 left-6 flex items-center gap-1.5 text-[#52525B] hover:text-white transition-colors z-10"
        data-testid="upload-flow-back"
      >
        <ChevronLeft size={16} />
        <span className="text-[11px] font-medium">Atrás</span>
      </button>

      {/* Step indicator */}
      <div className="absolute top-6 right-6 flex gap-1.5 z-10">
        {['upload', 'legal', 'scanning', 'done'].map((s, i) => (
          <div
            key={s}
            className="w-2 h-2 rounded-full transition-all"
            style={{
              background: ['upload', 'legal', 'scanning', 'done'].indexOf(step) >= i ? '#F5C842' : '#27272A',
            }}
          />
        ))}
      </div>

      <div className="w-full max-w-sm relative z-10">

        {/* ── UPLOAD STEP ── */}
        {step === 'upload' && (
          <div className="producer-fade-in">
            <div className="text-center mb-6">
              <Headphones size={28} className="text-[#00D4FF] mx-auto mb-3" />
              <p className="text-[10px] text-[#3F3F46] uppercase tracking-[0.3em]">Sube tu canción</p>
            </div>

            <div
              data-testid="upload-dropzone"
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                dragOver ? 'border-[#00D4FF] bg-[#00D4FF10]' : 'border-[#27272A] hover:border-[#52525B]'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
              />

              {uploading ? (
                <div className="space-y-3">
                  <div className="w-10 h-10 border-2 border-[#00D4FF] border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-xs text-[#A1A1AA]">{file?.name}</p>
                  <div className="h-1 bg-[#1A1A1F] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#00D4FF] rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-[#52525B]">{Math.round(uploadProgress)}%</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload size={24} className="text-[#52525B] mx-auto" />
                  <p className="text-xs text-[#71717A]">Arrastra tu archivo aquí</p>
                  <p className="text-[10px] text-[#3F3F46]">MP3, WAV, OGG, FLAC · Máx 50MB</p>
                </div>
              )}
            </div>

            {scanError && (
              <div className="mt-3 flex items-center gap-2 text-red-400 text-[11px] bg-red-400/10 rounded-xl px-3 py-2">
                <AlertTriangle size={12} />
                <span>{scanError}</span>
              </div>
            )}
          </div>
        )}

        {/* ── LEGAL STEP ── */}
        {step === 'legal' && (
          <div className="producer-fade-in">
            <div className="text-center mb-6">
              <Shield size={28} className="text-[#F5C842] mx-auto mb-3" />
              <p className="text-[10px] text-[#3F3F46] uppercase tracking-[0.3em]">Derechos</p>
            </div>

            {/* File info */}
            <div className="flex items-center gap-3 bg-[#141417] border border-[#27272A] rounded-xl p-3 mb-4">
              <Music2 size={16} className="text-[#00D4FF] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white truncate">{file?.name}</p>
                <p className="text-[10px] text-[#52525B]">{(file?.size / (1024 * 1024)).toFixed(1)} MB</p>
              </div>
              <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
            </div>

            {/* Legal checkbox */}
            <label
              className="flex items-start gap-3 p-4 rounded-xl border border-[#27272A] bg-[#0F0F12] cursor-pointer hover:border-[#F5C84240] transition-colors"
              data-testid="legal-checkbox-label"
            >
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                className="mt-0.5 accent-[#F5C842]"
                data-testid="legal-checkbox"
              />
              <span className="text-[11px] text-[#A1A1AA] leading-relaxed">
                Declaro que tengo los derechos o autorización sobre esta obra musical y acepto los términos de uso de Pulse Producer.
              </span>
            </label>

            <button
              data-testid="legal-continue-btn"
              onClick={handleLegalAndScan}
              disabled={!legalAccepted}
              className="w-full mt-4 py-3 rounded-xl text-xs font-bold transition-all"
              style={{
                background: legalAccepted ? 'linear-gradient(135deg, #F5C842, #FF6B00)' : '#1A1A1F',
                color: legalAccepted ? '#000' : '#52525B',
              }}
            >
              Continuar
            </button>
          </div>
        )}

        {/* ── SCANNING STEP ── */}
        {step === 'scanning' && (
          <div className="producer-fade-in text-center">
            <div className="producer-scan-pulse inline-block rounded-full p-5 mb-4">
              <Search size={28} className="text-[#F5C842]" />
            </div>
            <p className="text-sm font-medium text-white mb-2">Analizando tu canción...</p>
            <p className="text-[10px] text-[#52525B]">Verificando originalidad y preparando el entorno</p>

            <div className="mt-6 h-1 bg-[#1A1A1F] rounded-full overflow-hidden">
              <div className="h-full rounded-full producer-shimmer" style={{ width: '100%' }} />
            </div>
          </div>
        )}

        {/* ── DONE STEP ── */}
        {step === 'done' && (
          <div className="producer-fade-in text-center">
            <CheckCircle2 size={36} className="text-green-400 mx-auto mb-4" />
            <p className="text-sm font-medium text-white mb-1">Tu cancion esta lista</p>
            <p className="text-[10px] text-[#52525B] mb-2">
              {scanResult?.scan?.status === 'match'
                ? 'Contenido analizado — sin problemas detectados'
                : scanResult?.flagged
                ? 'No se pudo verificar derechos — declaracion de responsabilidad activa'
                : 'Analisis completado'}
            </p>

            {scanResult?.flagged && (
              <div className="flex items-start gap-2 bg-amber-500/10 rounded-xl p-3 mb-4 text-left">
                <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-300/80">
                  El sistema no pudo verificar los derechos de este audio. Al continuar, confirmas que tienes los derechos necesarios.
                </p>
              </div>
            )}

            <button
              data-testid="enter-studio-btn"
              onClick={handleEnterStudio}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-black transition-all active:scale-[0.97]"
              style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}
            >
              Entrar al estudio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// end of file
